import { useState } from 'react'
import { Alert, AutoComplete, Button, Card, Descriptions, Space, Statistic, Tag, theme, Tooltip, Typography } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatBytes, formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { toMoney } from '@/shared/api/schemas'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { DebtLimitModal } from './DebtLimitModal'
import { setSubscriberRateLimit } from './subscriberApi'
import { useSubscriberOverview } from './useSubscriberOverview'

/**
 * Qo'lda qo'yiladigan tezlik uchun TAYYOR variantlar.
 *
 * ⚠️ Ro'yxat erkin kiritishni ALMASHTIRMAYDI, unga qo'shiladi
 * (`AutoComplete`). Tarif rejalari xilma-xil va "faqat ro'yxatdan tanla"
 * qoidasi operatorni panel tashqarisiga (bazaga) haydab yuborardi —
 * ya'ni tekshiruvsiz yo'lga.
 */
const RATE_PRESETS = ['128k/128k', '1M/1M', '2M/2M', '5M/5M', '10M/10M', '20M/20M', '50M/50M', '100M/100M']

/**
 * Qo'lda kiritilgan tezlikning shakli.
 *
 * ⚠️ Backend naqshi bilan AYNAN bir xil bo'lishi shart
 * (`SubscriberServiceImpl.RATE_LIMIT_SHAPE` va bazadagi `CHECK`,
 * migratsiya `V202608080100`). Bu yerdagisi faqat TEZ va tushunarli
 * javob uchun — yagona kafolat baribir bazada.
 *
 * Oxiridagi ixtiyoriy qism — MikroTik burst zanjiri
 * (`rx/tx rx-burst/tx-burst … priority`).
 */
const RATE_SHAPE = /^[0-9]+[kKmMgG]?\/[0-9]+[kKmMgG]?( +([0-9]+[kKmMgG]?\/[0-9]+[kKmMgG]?|[0-9]+))*$/

/**
 * `Mikrotik-Rate-Limit` qiymatini bo'laklarga ajratadi.
 *
 * Shakl `rx/tx` va u ROUTER nuqtai nazaridan: `rx` — abonent yuborayotgan
 * (upload), `tx` — abonentga borayotgan (download).
 *
 * @returns `null` — qiymat yo'q yoki shakli tanib bo'lmaydi; chaqiruvchi
 *          unda xom qiymatni ko'rsatadi.
 */
function parseRateLimit(raw: string | null): { down: string; up: string; burst: string | null } | null {
  if (!raw) return null
  const [main, ...rest] = raw.trim().split(/\s+/)
  const pair = main?.split('/')
  if (!pair || pair.length !== 2 || !pair[0] || !pair[1]) return null
  return { up: pair[0], down: pair[1], burst: rest.length > 0 ? rest.join(' ') : null }
}

/** `50M` -> `50 Mbit`; tanib bo'lmasa xom qiymat o'z holicha. */
function prettySpeed(value: string): string {
  const match = /^(\d+)([kKmMgG])?$/.exec(value)
  if (!match?.[1]) return value
  return match[2] ? `${match[1]} ${match[2].toUpperCase()}bit` : `${match[1]} bit`
}

/**
 * Abonentning TO'LIQ manzarasi: balans, qoldiq, tezlik, sessiya.
 *
 * <h2>⚠️ Nima uchun bu karta bor</h2>
 * Ilgari operator bu ma'lumotlarni ko'rish uchun bazaga kirishi kerak
 * edi. Natijada eng ko'p uchraydigan savolga — "abonent nega
 * shikoyat qilyapti?" — panelda javob yo'q edi.
 *
 * <h2>⚠️ ENG MUHIM ELEMENT — "sessiya eski tarifda" ogohlantirishi</h2>
 * Tarif o'zgarganda abonent DARHOL yangi tezlikni olishi shart emas:
 * CoA routerga yetmagan bo'lishi mumkin. O'shanda baza to'g'ri, abonent
 * esa eski tezlikda ishlaydi.
 *
 * <p>2026-08-07 da aynan shunday bo'ldi: tarif 10M dan 100M ga
 * o'zgartirildi, panel "o'zgardi" deb ko'rsatdi, abonent esa eski
 * tezlikda qoldi. Buni faqat tezlik o'lchab bilish mumkin edi.
 *
 * <p>Endi bu holat KARTOCHKADA, sariq ogohlantirish bilan ko'rinadi.
 */
export function SubscriberOverviewCard({
  subscriberId,
  active = true,
}: {
  subscriberId: string
  /**
   * Karta HOZIR ko'rinib turibdimi (kartochkadagi faol bo'lim).
   *
   * ⚠️ Bu ko'rinishga emas, YANGILANISHGA ta'sir qiladi: `false` bo'lganda
   * 15 soniyalik davriy so'rov to'xtaydi. Bo'lim almashtirilganda karta
   * mount holida qoladi (`Tabs` uni keshda saqlaydi) va usiz operator
   * boshqa bo'limda ishlayotganda ham ko'rinmaydigan karta uchun
   * daqiqasiga to'rtta so'rov ketaverardi.
   *
   * Standart `true` — chaqiruvchi bo'lim tushunchasini bilmasa xatti-harakat
   * o'zgarmasin.
   */
  active?: boolean
}) {
  const { t } = useTranslation()
  // Rang qotirilmaydi: panel yorug' va qorong'i rejimda ham ishlaydi
  const { token } = theme.useToken()

  const query = useSubscriberOverview(subscriberId, active)
  const [debtLimitOpen, setDebtLimitOpen] = useState(false)

  if (query.isError) {
    return (
      <Card title={t('subscriber.overview.title')}>
        <Alert type="error" showIcon message={translateError(query.error, t)} />
      </Card>
    )
  }

  const d = query.data

  return (
    <Card title={t('subscriber.overview.title')} loading={query.isLoading}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/*
          ⚠️ Ogohlantirish ENG TEPADA va u faqat haqiqiy holatda chiqadi.
          Pastga qo'yilsa yoki doim ko'rinsa — e'tibordan qoladi.
        */}
        {d?.staleSession ? (
          <Alert
            type="warning"
            showIcon
            message={t('subscriber.overview.staleTitle')}
            description={t('subscriber.overview.staleBody')}
          />
        ) : null}

        <Space size="large" wrap>
          {/*
            ⚠️ Balans YO'Q bo'lishi = NOMA'LUM, nol emas.

            Ledger javob bermaganda backend ilgari `0` qaytarardi va bu
            yerda "0 so'm" yozilardi. Operator uni haqiqiy nol deb qabul
            qilib, abonentni bloklashi yoki agentga "pul tushmagan"
            deyishi mumkin edi — xato esa faqat log'da qolardi.

            Kartochka nosozlik paytida ATAYLAB ochiladi (qolgan maydonlar
            kerak), lekin yolg'on raqam ko'rsatishga haqqi yo'q.
          */}
          <Statistic
            title={t('subscriber.overview.balance')}
            value={
              d === undefined
                ? '—'
                : d.balance == null
                  ? t('billing.unknown')
                  : formatMoneyValue(toMoney(d.balance))
            }
            valueStyle={d && d.balance == null ? { color: token.colorTextTertiary } : undefined}
          />
          <Statistic
            title={
              <Space size={4}>
                {t('subscriber.debtLimit.title')}
                <Can permission="subscriber.debt.limit">
                  <Button size="small" type="link" onClick={() => setDebtLimitOpen(true)} disabled={d?.debtLimit == null}>
                    {t('common.edit')}
                  </Button>
                </Can>
              </Space>
            }
            value={d === undefined ? '—' : d.debtLimit == null ? t('billing.unknown') : formatMoneyValue(toMoney(d.debtLimit))}
            valueStyle={d && d.debtLimit == null ? { color: token.colorTextTertiary } : undefined}
          />
          {/*
            ⚠️ `remainingBytes = null` UCH xil holatni bildiradi va
            ularni faqat `hasUsableGrant` ajratadi:

              grant yo'q      -> "Paket yo'q"   (kvota TUGAGAN ham shu)
              grant bor, null -> "Cheksiz"
              grant bor, son  -> qoldiq

            Ilgari bu yerda faqat `null` tekshirilardi va kvotasi
            TUGAGAN abonent kartochkasida "Cheksiz" yozilardi. Sabab:
            tugagan grant `usable` to'plamdan CHIQADI, ya'ni qiymat
            `0` dan o'tmasdan to'g'ridan-to'g'ri `null` ga sakraydi.

            Operator shikoyat qilgan abonentni ochib, "Cheksiz trafik"
            ko'rardi — tashxis TESKARI bo'lardi. Pastdagi paketlar
            kartasi esa "Faol paket yo'q" derdi: bitta ekran o'ziga
            zid gapirardi.
          */}
          {/*
            ⚠️ NOM SHU YERDA ANIQLASHTIRILDI, chunki bitta ekranda IKKI
            xil "Qolgan trafik" bor edi: bu yerda 21 GB, pastdagi
            "Paketlar" kartasida 6 GB. Ikkalasi ham to'g'ri — bu
            BARCHA amaldagi grantlar YIG'INDISI
            (`SubscriberOverview.remainingBytes` javadoc'i), pastdagisi
            esa faqat hozir sarflanayotgan grant. Bir xil nom operatorni
            "qaysi biri yolg'on?" degan savolga olib borardi.
          */}
          <Statistic
            title={
              <Tooltip title={t('subscriber.overview.remainingHint')}>
                <span>{t('subscriber.overview.remaining')}</span>
              </Tooltip>
            }
            value={
              d === undefined
                ? '—'
                : !d.hasUsableGrant
                  ? t('subscriber.overview.noPackage')
                  : d.remainingBytes === null || d.remainingBytes === undefined
                    ? t('billing.unlimited')
                    : formatBytes(d.remainingBytes)
            }
            valueStyle={d && !d.hasUsableGrant ? { color: '#cf1322' } : undefined}
          />
          {/*
            ⚠️ Tezlik — KO'RSATISH emas, BOSHQARISH joyi.
            Operator kartochkada qiymatni ko'rib, uni o'zgartirish
            uchun boshqa sahifaga o'tishi kerak bo'lsa, u umuman
            o'zgartirmaydi. Shuning uchun tahrir shu yerda.
          */}
          <RateLimitStat
            subscriberId={subscriberId}
            rateLimit={d?.rateLimit ?? null}
            override={d?.rateLimitOverride ?? null}
            loading={d === undefined}
          />
          <Statistic
            title={t('subscriber.overview.sessions')}
            value={d?.activeSessions ?? 0}
          />
        </Space>

        <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
          <Descriptions.Item label={t('subscriber.overview.trafikBalance')}>
            {/* Balans bilan bir xil qoida: `null` = noma'lum, "0 B" emas */}
            {d === undefined
              ? '—'
              : d.trafikBalance == null
                ? t('billing.unknown')
                : formatBytes(d.trafikBalance)}
          </Descriptions.Item>
          <Descriptions.Item label={t('subscriber.overview.accessUntil')}>
            {/*
              ⚠️ Bu yerda ham `null` ikki ma'noli edi va YASHIL
              "Cheksiz" yorlig'i chiqardi — muddati TUGAGAN abonent
              uchun ham. Yashil rang "hammasi joyida" degan signal,
              ya'ni xato eng yomon shaklda ko'rsatilardi.
            */}
            {d?.accessUntil
              ? <Tooltip title={utcTooltip(d.accessUntil)}>{formatDateTime(d.accessUntil)}</Tooltip>
              : d && !d.hasUsableGrant
                ? <Tag color="red">{t('subscriber.overview.noPackage')}</Tag>
                : <Tag color="green">{t('billing.unlimited')}</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label={t('subscriber.overview.sessionStarted')}>
            {d?.sessionStartedAt
              ? <Tooltip title={utcTooltip(d.sessionStartedAt)}>{formatDateTime(d.sessionStartedAt)}</Tooltip>
              : <Typography.Text type="secondary">{t('subscriber.overview.noSession')}</Typography.Text>}
          </Descriptions.Item>
          <Descriptions.Item label={t('subscriber.overview.tariffChanged')}>
            {d?.tariffChangedAt
              ? <Tooltip title={utcTooltip(d.tariffChangedAt)}>{formatDateTime(d.tariffChangedAt)}</Tooltip>
              : '—'}
          </Descriptions.Item>
        </Descriptions>
      </Space>
      <DebtLimitModal
        subscriberId={subscriberId}
        open={debtLimitOpen}
        balance={d?.balance ? toMoney(d.balance) : null}
        debtLimit={d?.debtLimit ? toMoney(d.debtLimit) : null}
        onClose={() => setDebtLimitOpen(false)}
      />
    </Card>
  )
}

/**
 * Tezlik — ko'rsatiladi VA shu yerda o'zgartiriladi.
 *
 * <h2>⚠️ Ikki qiymat, ikki xil ma'no</h2>
 * <ul>
 *   <li>`rateLimit` — AMALDAGI tezlik. Override bo'lsa o'sha, aks
 *       holda paketniki;</li>
 *   <li>`override` — QO'LDA qo'yilganmi (`null` = paketdan).</li>
 * </ul>
 *
 * Ikkisini ajratib ko'rsatish shart. Aks holda operator paketni
 * almashtirib, "nega tezlik o'zgarmadi?" degan savolga tushadi —
 * aslida override ustun turgan bo'ladi va buni hech narsa aytmaydi.
 *
 * <h2>⚠️ Nega faol sessiya emas, BAZA yangilanadi</h2>
 * Sessiya boshqaruvidagi CoA endpointi ham tezlik o'zgartiradi,
 * lekin u DOIMIY EMAS: abonent qayta ulanganda paket tezligi
 * qaytadi. Bu yerdagi amal bazaga yozadi va KEYIN CoA yuboradi
 * (CLAUDE.md §3.2), ya'ni o'zgarish qayta ulanishdan keyin ham
 * saqlanadi.
 */
function RateLimitStat({
  subscriberId,
  rateLimit,
  override,
  loading,
}: {
  subscriberId: string
  rateLimit: string | null
  override: string | null
  loading: boolean
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [formatError, setFormatError] = useState<string | null>(null)
  const runner = useOperationRunner([queryKeys.subscribers.overview(subscriberId)])

  /**
   * X-SIB-003 — qo'lda kiritilgan qiymat TEKSHIRILADI.
   *
   * ⚠️ Ilgari maydon butunlay erkin edi: operator "2 mbit" yoki "50"
   * yozib saqlashi mumkin edi. Backend uni rad etardi
   * (`SubscriberServiceImpl.setRateLimitOverride`), lekin bu bir
   * marta serverga borib kelishni talab qilardi va xato modal
   * ostidagi umumiy ogohlantirishda chiqardi.
   *
   * ⚠️ Bo'sh qiymat — XATO EMAS: u "qo'lda qo'yilganini olib tashla"
   * degani va backend ham aynan shunday talqin qiladi.
   */
  const save = async (raw: string) => {
    const clean = raw.trim()
    if (clean !== '' && !RATE_SHAPE.test(clean)) {
      setFormatError(t('subscriber.overview.rateLimitInvalid'))
      return
    }
    setFormatError(null)
    const outcome = await runner.run(() => setSubscriberRateLimit(subscriberId, clean || null))
    if (outcome.status !== 'FAILED') {
      setEditing(false)
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.overview(subscriberId) })
    }
  }

  if (editing) {
    return (
      <div style={{ minWidth: 280 }}>
        <Typography.Text type="secondary" style={{ fontSize: 14 }}>
          {t('subscriber.overview.rateLimit')}
        </Typography.Text>
        <Space.Compact style={{ width: '100%', marginTop: 4 }}>
          {/*
            ⚠️ `AutoComplete`, `Select` EMAS.

            "Tanlanadigan qiymatlar bo'lsin" talabi bilan "tarif rejalari
            xilma-xil" haqiqati orasidagi yagona to'g'ri yechim shu: eng
            ko'p ishlatiladigan qiymatlar bir bosishda, lekin istalgan
            qiymatni ham yozish mumkin. Sof `Select` operatorni panel
            tashqarisiga — to'g'ridan-to'g'ri bazaga — haydab yuborardi,
            ya'ni HECH QANDAY tekshiruvsiz yo'lga.
          */}
          <AutoComplete
            autoFocus
            style={{ width: '100%' }}
            value={value}
            options={RATE_PRESETS.map((preset) => ({ value: preset }))}
            filterOption={(input, option) =>
              (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
            }
            placeholder="50M/50M"
            status={formatError ? 'error' : undefined}
            onChange={(next: string) => {
              setValue(next)
              if (formatError) setFormatError(null)
            }}
            onSelect={(next: string) => void save(next)}
          />
          <Button type="primary" loading={runner.running} onClick={() => void save(value)}>
            {t('common.save')}
          </Button>
          <Button onClick={() => { setEditing(false); setFormatError(null) }}>
            {t('common.cancel')}
          </Button>
        </Space.Compact>
        {formatError ? (
          <Typography.Paragraph type="danger" style={{ fontSize: 12, margin: '4px 0 0' }}>
            {formatError}
          </Typography.Paragraph>
        ) : null}
        {/*
          ⚠️ Bo'sh qoldirib saqlash — "qo'lda qo'yilganini olib tashlash".
          Buni aytib qo'yish shart: aks holda operator maydonni
          tozalashdan qo'rqadi va eski qiymat abadiy qolib ketadi.
        */}
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 4, marginBottom: 0 }}>
          {t('subscriber.overview.rateLimitHint')}
        </Typography.Paragraph>
        {runner.result ? (
          <div style={{ marginTop: 8 }}>
            <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
          </div>
        ) : null}
      </div>
    )
  }

  const parsed = parseRateLimit(rateLimit)

  return (
    <div>
      <Statistic
        title={
          <Space size={4}>
            {t('subscriber.overview.rateLimit')}
            {override ? (
              <Tooltip title={t('subscriber.overview.rateLimitManualHint')}>
                <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                  {t('subscriber.overview.rateLimitManual')}
                </Tag>
              </Tooltip>
            ) : null}
          </Space>
        }
        /*
          ⚠️ MATN "CHEKLOV YO'Q" EDI VA U TESKARI MA'NO BERARDI.

          Operator uni "cheklovsiz, ya'ni cheksiz tezlik" deb o'qiydi.
          Aslida esa buning teskarisi: amaldagi paket tezlik bermayapti,
          va RADIUS bunday abonentni "Tarif biriktirilmagan" deb RAD
          ETADI (`DefaultRadiusAuthorizeService`). Ya'ni eng yomon holat
          eng yaxshi holatdek yozilgan edi.

          Rang qizil bo'lgani yetmaydi — so'z rangdan kuchliroq.
        */
        /*
          ⚠️ BUG-04 — XOM QIYMAT KO'RSATILMAYDI.

          `Mikrotik-Rate-Limit` da burst bo'lsa qiymat "1m/1m 2m/2m"
          ko'rinishida keladi va u ikki qiymat qo'shilib ketgandek
          o'qiladi. Endi asosiy tezlik ikki yo'nalish bilan aniq
          yoziladi, burst esa pastda alohida qatorda (u yo'qolmasligi
          kerak — routerga aynan shu boradi).

          ⚠️ Shakl tanib bo'lmasa XOM qiymat qaytadi (`parsed === null`):
          "tushunmadim" degan sabab bilan ma'lumotni yashirish
          operatorni tashxisdan mahrum qilardi.
        */
        value={
          loading
            ? '—'
            : !rateLimit
              ? t('subscriber.overview.noRateLimit')
              : parsed
                ? `↓ ${prettySpeed(parsed.down)} · ↑ ${prettySpeed(parsed.up)}`
                : rateLimit
        }
        valueStyle={!loading && !rateLimit ? { color: '#cf1322' } : undefined}
      />
      {!loading && parsed?.burst ? (
        // Burst — ALOHIDA qator. U asosiy tezlikning bir qismi emas,
        // "qisqa vaqtga ruxsat etilgan yuqori tezlik" degan boshqa
        // tushuncha; bitta satrga qo'shib qo'yish aynan shu chalkashlikni
        // tug'dirgan edi.
        <Typography.Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 12 }}>
          {t('subscriber.overview.rateLimitBurst', { value: parsed.burst })}
        </Typography.Paragraph>
      ) : null}
      {!loading && !rateLimit ? (
        <Typography.Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 12 }}>
          {t('subscriber.overview.noRateLimitHint')}
        </Typography.Paragraph>
      ) : null}
      <Can permission="subscriber.ratelimit.set">
        <Button
          size="small"
          type="link"
          style={{ paddingInline: 0 }}
          onClick={() => {
            setValue(override ?? rateLimit ?? '')
            setEditing(true)
          }}
        >
          {t('subscriber.overview.rateLimitEdit')}
        </Button>
      </Can>
      {runner.result ? (
        <div style={{ marginTop: 8, maxWidth: 320 }}>
          <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
        </div>
      ) : null}
    </div>
  )
}
